import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity,
  ArrowRight,
  BadgeCheck,
  BriefcaseMedical,
  HeartPulse,
  Hospital,
  Lock,
  Mail,
  MapPin,
  Menu,
  Phone,
  Shield,
  Sparkles,
  Stethoscope,
  Users,
  Workflow,
  X,
} from "lucide-react";
import useFrontendMeta from "../hooks/useFrontendMeta";
import { ROUTE_PATHS } from "../constants/routeConstants";
import { Footer } from "../Pages/User/components/layout/Footer";

const featureCards = [
  {
    icon: <Users className="h-6 w-6" />,
    title: "Patient Management",
    text: "Coordinate every patient journey with clear records, secure access, and seamless workflows.",
  },
  {
    icon: <BriefcaseMedical className="h-6 w-6" />,
    title: "Medical Records",
    text: "Keep clinical data organized with modern documentation, quick retrieval, and audit-ready history.",
  },
  {
    icon: <Shield className="h-6 w-6" />,
    title: "Secure Healthcare System",
    text: "Protect sensitive information with role-based controls, encrypted operations, and trusted access.",
  },
];

const platformFeatures = [
  "Patient Registration",
  "Appointment Tracking",
  "Unit Management",
  "Medical Documentation",
  "Reporting",
  "Secure Access Control",
];

const stats = [
  { value: "12k+", label: "Patients Served" },
  { value: "48", label: "Healthcare Units" },
  { value: "96%", label: "Medical Records Accuracy" },
  { value: "220+", label: "Active Users" },
];

const contactCards = [
  { icon: <Mail className="h-5 w-5" />, title: "Email", value: "hello@mhuhealthcare.com" },
  { icon: <Phone className="h-5 w-5" />, title: "Phone", value: "+91 98765 43210" },
  { icon: <MapPin className="h-5 w-5" />, title: "Address", value: "Hyderabad, Telangana, India" },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const meta = useFrontendMeta("/");
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileMenu(false);
  };

  const footerProps = useMemo(
    () => ({
      description: meta?.footer?.description,
      socialLinks: meta?.footer?.socialLinks || [],
      contact: meta?.footer?.contact,
      siteMeta: meta,
    }),
    [meta],
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-[linear-gradient(180deg,#f7fff9_0%,#eefcf6_45%,#f7fffb_100%)] text-slate-900">
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "border-b border-emerald-100 bg-white/85 shadow-xl shadow-emerald-100/80 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 text-slate-900">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 via-green-300 to-lime-200 shadow-xl shadow-emerald-500/20">
              <HeartPulse className="h-5 w-5 text-emerald-950" />
            </div>
            <div>
              <p className="text-lg font-semibold tracking-wide">MHU Healthcare</p>
              <p className="text-[11px] uppercase tracking-[0.25em] text-emerald-700/80">Mobile hospital platform</p>
            </div>
          </Link>

          <div className="hidden items-center gap-8 lg:flex">
            <button onClick={() => scrollToSection("about")} className="text-sm font-medium text-slate-700 hover:text-emerald-700 transition">About</button>
            <button onClick={() => scrollToSection("contact")} className="text-sm font-medium text-slate-700 hover:text-emerald-700 transition">Contact Us</button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(ROUTE_PATHS.LOGIN)}
              className="hidden rounded-full bg-gradient-to-r from-emerald-500 to-green-400 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-200 transition hover:-translate-y-0.5 lg:inline-flex"
            >
              Login
            </button>
            <button
              onClick={() => setMobileMenu((prev) => !prev)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-emerald-100 bg-white text-slate-700 shadow-sm lg:hidden"
              aria-label="Toggle menu"
            >
              {mobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>

        {mobileMenu && (
          <div className="border-t border-emerald-100 bg-white/95 px-4 py-4 shadow-lg shadow-emerald-100/70 backdrop-blur-xl lg:hidden">
            <div className="flex flex-col gap-3 text-sm text-slate-700">
              <button onClick={() => scrollToSection("about")} className="text-left">About</button>
              <button onClick={() => scrollToSection("contact")} className="text-left">Contact Us</button>
              <button onClick={() => navigate(ROUTE_PATHS.LOGIN)} className="rounded-full bg-gradient-to-r from-emerald-500 to-green-400 px-4 py-2 font-semibold text-white">Login</button>
            </div>
          </div>
        )}
      </header>

      <main>
        <section className="relative isolate overflow-hidden px-4 pb-16 pt-6 sm:px-6 lg:px-8 lg:pb-24 lg:pt-10">
          <div className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute left-[-8%] top-10 h-56 w-56 rounded-full bg-emerald-400/15 blur-3xl" />
            <div className="absolute right-[-10%] top-0 h-72 w-72 rounded-full bg-lime-400/10 blur-3xl" />
            <div className="absolute bottom-[-8%] left-1/3 h-56 w-56 rounded-full bg-cyan-400/10 blur-3xl" />
          </div>

          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800 shadow-sm shadow-emerald-100/80">
                <Sparkles className="h-4 w-4 text-emerald-600" />
                Mobile hospital care, made simple and trusted.
              </div>
              <div className="space-y-5">
                <h1 className="max-w-2xl text-4xl font-black tracking-tight text-slate-900 md:text-5xl lg:text-6xl">
                  A mobile hospital platform for smarter patient care and faster operations.
                </h1>
                <p className="max-w-xl text-base text-slate-600 md:text-lg">
                  Bring together patient registration, records, appointments, and secure workflows in one calm, modern healthcare experience.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate(ROUTE_PATHS.LOGIN)}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 via-green-400 to-lime-300 px-6 py-3 text-sm font-semibold text-white shadow-xl shadow-emerald-200 transition hover:-translate-y-0.5"
                >
                  Get Started <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => scrollToSection("about")}
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 shadow-lg shadow-emerald-100 transition hover:-translate-y-0.5 hover:bg-emerald-50"
                >
                  Learn More
                </button>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  { label: "Instant onboarding", icon: <BadgeCheck className="h-4 w-4" /> },
                  { label: "Secure workflows", icon: <Lock className="h-4 w-4" /> },
                  { label: "Clinical visibility", icon: <Activity className="h-4 w-4" /> },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-lg shadow-emerald-100/80">
                    <div className="mb-2 flex items-center gap-2 text-emerald-700">{item.icon}<span className="text-xs uppercase tracking-[0.2em]">{item.label}</span></div>
                    <p className="text-sm text-slate-600">Built for dependable, high-trust mobile healthcare operations.</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative mx-auto w-full max-w-xl">
              <div className="rounded-[32px] border border-emerald-100 bg-white p-4 shadow-[0_24px_60px_rgba(52,211,153,0.18)]">
                <div className="rounded-[28px] border border-emerald-100 bg-[linear-gradient(145deg,#ecfff6_0%,#ffffff_45%,#effcf5_100%)] p-5">
                  <div className="mb-5 flex items-center justify-between rounded-2xl border border-emerald-100 bg-white/90 p-4 text-sm text-slate-700 shadow-sm">
                    <span className="flex items-center gap-2"><Hospital className="h-4 w-4 text-emerald-600" /> Live care dashboard</span>
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-emerald-700">Online</span>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-3xl border border-emerald-100 bg-white p-5 shadow-sm shadow-emerald-100/90">
                      <p className="text-xs uppercase tracking-[0.2em] text-emerald-700/80">Today</p>
                      <p className="mt-2 text-3xl font-black text-slate-900">184</p>
                      <p className="mt-1 text-sm text-slate-600">Patient visits managed</p>
                    </div>
                    <div className="rounded-3xl border border-cyan-100 bg-cyan-50 p-5 shadow-sm shadow-cyan-100/80">
                      <p className="text-xs uppercase tracking-[0.2em] text-cyan-700/80">Records</p>
                      <p className="mt-2 text-3xl font-black text-slate-900">93%</p>
                      <p className="mt-1 text-sm text-slate-600">Documentation completion</p>
                    </div>
                    <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-5 md:col-span-2">
                      <div className="mb-3 flex items-center justify-between text-sm text-slate-700">
                        <span>Care coordination</span>
                        <span className="font-semibold text-emerald-700">+18% efficiency</span>
                      </div>
                      <div className="h-2 rounded-full bg-white">
                        <div className="h-2 w-[78%] rounded-full bg-gradient-to-r from-emerald-500 to-lime-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: [0, -8, 0], opacity: 1 }} transition={{ duration: 3.2, repeat: Infinity, repeatType: "mirror" }} className="absolute -left-6 top-8 rounded-2xl border border-emerald-100 bg-white p-4 shadow-xl shadow-emerald-100/90">
                <div className="flex items-center gap-3"><Stethoscope className="h-5 w-5 text-emerald-600" /><span className="text-sm font-semibold text-slate-800">Clinical overview</span></div>
              </motion.div>
              <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: [0, 8, 0], opacity: 1 }} transition={{ duration: 3.6, repeat: Infinity, repeatType: "mirror", delay: 0.4 }} className="absolute -right-4 bottom-8 rounded-2xl border border-cyan-100 bg-white p-4 shadow-xl shadow-cyan-100/90">
                <div className="flex items-center gap-3"><Workflow className="h-5 w-5 text-cyan-700" /><span className="text-sm font-semibold text-slate-800">Smart automation</span></div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section id="about" className="px-4 py-8 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-7xl">
            <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} className="mb-8 text-center">
              <p className="text-sm uppercase tracking-[0.3em] text-emerald-700/80">About</p>
              <h2 className="mt-3 text-3xl font-black text-slate-900 md:text-4xl">Why mobile hospitals choose MHU</h2>
              <p className="mx-auto mt-4 max-w-3xl text-slate-600">A premium platform blending secure operations, medical content flow, and trusted analytics into one clear experience.</p>
            </motion.div>
            <div className="grid gap-6 md:grid-cols-3">
              {featureCards.map((card, idx) => (
                <motion.article key={card.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.25 }} transition={{ delay: idx * 0.08 }} whileHover={{ y: -6 }} className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-md shadow-emerald-100/70 transition">
                  <div className="mb-4 inline-flex rounded-2xl bg-emerald-50 p-3 text-emerald-700">{card.icon}</div>
                  <h3 className="text-xl font-semibold text-slate-900">{card.title}</h3>
                  <p className="mt-3 text-sm text-slate-600">{card.text}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-8 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-7xl rounded-[32px] border border-emerald-100 bg-white p-6 shadow-md shadow-emerald-100/70">
            <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.25 }} className="mb-8 text-center">
              <p className="text-sm uppercase tracking-[0.3em] text-emerald-700/80">Features</p>
              <h2 className="mt-3 text-3xl font-black text-slate-900 md:text-4xl">Everything you need for mobile hospital delivery</h2>
            </motion.div>
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {platformFeatures.map((feature, idx) => (
                <motion.article key={feature} initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.2 }} transition={{ delay: idx * 0.04 }} whileHover={{ y: -4 }} className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-md shadow-emerald-100/70">
                  <div className="mb-4 flex items-center gap-3 text-emerald-700"><BadgeCheck className="h-5 w-5 text-emerald-600" /> <span className="text-base font-semibold text-slate-900">{feature}</span></div>
                  <p className="text-sm text-slate-600">A focused module to improve care coordination, reporting, access, and daily operations.</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-8 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto grid max-w-7xl gap-8 rounded-[32px] border border-emerald-100 bg-[linear-gradient(145deg,#ecfff6_0%,#ffffff_50%,#f4fff8_100%)] p-6 shadow-md shadow-emerald-100/70 lg:grid-cols-[0.9fr_1.1fr] lg:p-8">
            <motion.div initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.25 }} className="space-y-4">
              <p className="text-sm uppercase tracking-[0.3em] text-emerald-700/80">Statistics</p>
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">Healthcare insights at a glance</h2>
              <p className="text-slate-600">Proven performance, trusted scale, and a platform designed to help institutions deliver better care every day.</p>
            </motion.div>
            <div className="grid gap-6 sm:grid-cols-2">
              {stats.map((item, idx) => (
                <motion.article key={item.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ delay: idx * 0.04 }} className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-md shadow-emerald-100/70">
                  <p className="text-4xl font-black text-slate-900">{item.value}</p>
                  <p className="mt-2 text-sm text-slate-600">{item.label}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="px-4 py-8 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-7xl">
            <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.25 }} className="mb-8 text-center">
              <p className="text-sm uppercase tracking-[0.3em] text-emerald-700/80">Contact</p>
              <h2 className="mt-3 text-3xl font-black text-slate-900 md:text-4xl">Get in touch with our mobile hospital team</h2>
            </motion.div>
            <div className="grid gap-6 md:grid-cols-3">
              {contactCards.map((item, idx) => (
                <motion.article key={item.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ delay: idx * 0.05 }} whileHover={{ y: -5 }} className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-md shadow-emerald-100/70 transition">
                  <div className="mb-4 inline-flex rounded-2xl bg-emerald-50 p-3 text-emerald-700">{item.icon}</div>
                  <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.value}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer {...footerProps} variant="landing" />
    </div>
  );
};

export default LandingPage;
